import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { canAccessPortalPage } from './portalAccessService';

// Internal user roles that have access to CRM
const INTERNAL_ROLES = ['Owner', 'Executive', 'Member', 'Contractor', 'Accountant', 'team'];

/** CRM sidebar + routes: Leads, Billing, Templates, Reports, Partners (Owner/Executive only). */
export const CRM_OWNER_EXECUTIVE_ROLES = ['Owner', 'Executive'];

// Portal user roles
const PORTAL_ROLES = ['client'];

/**
 * Get user data and determine user type
 * @param {string} userId - The user's UID
 * @returns {Promise<{userType: string, role: string, data: object}>}
 */
export const getUserData = async (userId) => {
  try {
    // Check internal_users collection first (CRM users)
    const internalUserDoc = await getDoc(doc(db, 'internal_users', userId));
    if (internalUserDoc.exists()) {
      const data = internalUserDoc.data();
      return {
        userType: 'internal',
        role: data.role,
        data: data
      };
    }

    // Check users collection (Portal users)
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        userType: 'portal',
        role: data.role,
        data: data
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Check if user is an internal user (has access to CRM)
 * @param {string} userId - The user's UID
 * @returns {Promise<boolean>}
 */
export const isInternalUser = async (userId) => {
  const userData = await getUserData(userId);
  return userData && userData.userType === 'internal';
};

/**
 * Check if user is a portal user (has access to Portal only)
 * @param {string} userId - The user's UID
 * @returns {Promise<boolean>}
 */
export const isPortalUser = async (userId) => {
  const userData = await getUserData(userId);
  return userData && userData.userType === 'portal';
};

/**
 * Check if user has a specific role
 * @param {string} userId - The user's UID
 * @param {string} role - The role to check for
 * @returns {Promise<boolean>}
 */
export const hasRole = async (userId, role) => {
  const userData = await getUserData(userId);
  return userData && userData.role === role;
};

/**
 * Check if user has any of the specified roles
 * @param {string} userId - The user's UID
 * @param {Array<string>} roles - Array of roles to check for
 * @returns {Promise<boolean>}
 */
export const hasAnyRole = async (userId, roles) => {
  const userData = await getUserData(userId);
  return userData && roles.includes(userData.role);
};

/**
 * Check if user has internal role (access to CRM)
 * @param {string} userId - The user's UID
 * @returns {Promise<boolean>}
 */
export const hasInternalRole = async (userId) => {
  const userData = await getUserData(userId);
  return userData && INTERNAL_ROLES.includes(userData.role);
};

/**
 * Check if user has portal role (access to Portal only)
 * @param {string} userId - The user's UID
 * @returns {Promise<boolean>}
 */
export const hasPortalRole = async (userId) => {
  const userData = await getUserData(userId);
  return userData && PORTAL_ROLES.includes(userData.role);
};

/**
 * Get the appropriate redirect path based on user type and account type
 * @param {string} userId - The user's UID
 * @returns {Promise<string>} - '/crm', '/portal', or '/start'
 */
export const getRedirectPath = async (userId) => {
  const userData = await getUserData(userId);
  if (!userData) {
    return '/login';
  }

  if (userData.userType === 'internal') {
    return '/crm';
  } else {
    // Portal user - check account type
    if (userData.data?.accountId) {
      try {
        const accountDoc = await getDoc(doc(db, 'accounts', userData.data.accountId));
        if (accountDoc.exists()) {
          const accountData = accountDoc.data();
          if (accountData.type === 'Clients' || accountData.type === 'clients') {
            // Client account - redirect to portal
            return '/portal';
          } else {
            // Lead account - redirect to start
            return '/start';
          }
        }
      } catch (error) {
        console.error('Error fetching linked account:', error);
      }
    }
    // No linked account or error fetching - default to start for lead intake
    return '/start';
  }
};

/**
 * Check if user can access a specific route
 * @param {string} userId - The user's UID
 * @param {string} route - The route to check access for
 * @returns {Promise<boolean>}
 */
export const canAccessRoute = async (userId, route) => {
  const userData = await getUserData(userId);
  if (!userData) {
    return false;
  }

  // CRM routes - only internal users can access
  if (route.startsWith('/crm')) {
    return userData.userType === 'internal';
  }

  // Portal routes - only portal users can access
  if (route.startsWith('/portal')) {
    if (userData.userType !== 'portal') {
      return false;
    }
    
    // Check specific portal page access
    return await canAccessPortalPage(userId, route);
  }

  // Public routes - everyone can access
  if (['/login', '/signup', '/terms', '/privacy'].includes(route)) {
    return true;
  }

  return false;
}; 